// @ts-nocheck
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { IconWrapper } from "@insite/mobius/Icon";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { BlockProps } from "@store/Library/Components/Toolkit/Block";
import { carouselDotWrapperStyles } from "@store/Themes/common/mixins/carousel";
import { transitionControlStyles, transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import { buttonResetProps } from "@store/Themes/common/props/button";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
/* Store Custom Code - Start */
// import ChevronDown from "@store/Library/Components/Toolkit/Icons/ChevronDown";
// import ChevronLeft from "@store/Library/Components/Toolkit/Icons/ChevronLeft";
// import ChevronRight from "@store/Library/Components/Toolkit/Icons/ChevronRight";
// import ChevronUp from "@store/Library/Components/Toolkit/Icons/ChevronUp";
/* Store Custom Code - End */
import { iconScalableStyles, spacingStyles } from "@toolkit/Themes/common/mixins";
import { css } from "styled-components";

/* Store Custom Code - Start */
import ArrowiOSBackward from "@store/Library/Components/Toolkit/Icons/ArrowiOSBackward";
import ArrowiOSDownward from "@store/Library/Components/Toolkit/Icons/ArrowiOSDownward";
import ArrowiOSForward from "@store/Library/Components/Toolkit/Icons/ArrowiOSForward";
import ArrowiOSUpward from "@store/Library/Components/Toolkit/Icons/ArrowiOSUpward";
import { contentWidthStyles } from "@store/Themes/common/mixins/common";
/* Store Custom Code - End */

export type CarouselArrowDirection = "up" | "right" | "down" | "left";

export const carouselGetArrowSrc = (direction: CarouselArrowDirection) => {
    switch (direction) {
        case "up":
            return ArrowiOSUpward;
        case "right":
            return ArrowiOSForward;
        case "down":
            return ArrowiOSDownward;
        case "left":
            return ArrowiOSBackward;
        default:
            return "";
    }
};

export const carouselGetArrowProps = (
    direction: CarouselArrowDirection,
    size = pxToRem(32),
): ButtonPresentationProps => ({
    ...buttonResetProps,
    icon: {
        src: carouselGetArrowSrc(direction),
        position: "left",
    },
    css: css`
        ${buttonResetProps.css}
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: ${size};
        width: 1em;
        height: 1em;
        border-radius: 50%;
        box-shadow: ${x => x.theme.shadows.e40};
        background-color: ${getColor("primary") /* Store Custom Code */};
        transition: ${transitionHoverStyles("color")};

        &:not(:disabled) {
            color: ${getColor("primary.contrast") /* Store Custom Code */};

            &:hover,
            &:active {
                color: ${getColor("primary.contrast") /* Store Custom Code */};
                background-color: ${getColor("primary.dark")}; /* Bunzl Custom Code */
            }
        }

        ${IconWrapper} {
            ${iconScalableStyles("0.63em")}
            top: 0;
            margin: 0;
        }
    `,
});

export const carouselGetDotProps = (selected?: boolean, color = "inherit"): ButtonPresentationProps => ({
    ...buttonResetProps,
    css: css`
        ${buttonResetProps.css}
        color: ${getColor(color)};
        background-color: ${selected ? getColor(color) : "transparent"};
        width: ${pxToRem(8)};
        height: ${pxToRem(8)};
        margin-left: ${pxToRem(8)};
        border-radius: 50%;
        transition: ${transitionControlStyles("color", "opacity")};
        border: 1px solid ${getColor(color)};
        &:first-child {
            margin-left: 0;
        }

        &:hover,
        &:active {
            color: ${getColor(color)};
            background-color: currentColor;
            opacity: 1;
        }
    `,
});

export const getArrowWrapperProps = (direction: "right" | "left"): InjectableCss => ({
    css: css`
        position: absolute;
        top: 50%;
        ${direction}: 0;
        z-index: 1;
        ${breakpointValues(
            [
                `translate3d(${direction === "left" ? "-" : ""}50%, -50%, 0)`,
                `translate3d(${direction === "left" ? "-" : ""}60%, -50%, 0)`,
            ],
            ["transform"],
        )}
    `,
});

export interface CarouselProps {
    titleText?: TypographyPresentationProps;
    block?: BlockProps;
    prevArrowWrapper?: InjectableCss;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowWrapper?: InjectableCss;
    nextArrowButton?: ButtonPresentationProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    dotsWrapper?: InjectableCss;
    dotButton?: ButtonPresentationProps;
    dotButtonSelected?: ButtonPresentationProps;
}

export const carouselProps: CarouselProps = {
    titleText: {
        variant: "h2",
    },
    prevArrowWrapper: getArrowWrapperProps("left"),
    prevArrowButton: carouselGetArrowProps("left"),
    nextArrowWrapper: getArrowWrapperProps("right"),
    nextArrowButton: carouselGetArrowProps("right"),
    carouselWrapper: {
        css: css`
            display: flex;
            position: relative;
            width: 100%;
            /* Bunzl Custom Code */
            ${contentWidthStyles()}
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
            width: 100%;
            /* Bunzl Custom Code */
            ${contentWidthStyles()}
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
            /* Bunzl Custom Code */
            max-width: 100%;
        `,
    },
    carouselSlide: {
        css: css`
            position: relative;
            flex: 0 0 auto;
            ${breakpointValues([null, null, pxToRem(0, 15)], "padding")};
        `,
    },
    carouselSlideInner: {
        css: css`
            position: relative;
            display: flex;
            flex-direction: column;
            width: 100%;
            height: 100%;
        `,
    },
    dotsWrapper: {
        css: css`
            ${carouselDotWrapperStyles()}
            ${spacingStyles(pxToRem(30), null)}
        `,
    },
    dotButton: carouselGetDotProps(false, "primary"),
    dotButtonSelected: carouselGetDotProps(true, "secondary"),
};
